import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sun, Loader2, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/format";
import { todayIso } from "@/lib/cantieri";
import { localeTag } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ReportSections } from "@/components/home/ReportSections";

const longDate = () => new Date().toLocaleDateString(localeTag(), { weekday: "long", day: "numeric", month: "long" });
const sig = (d) => ["appuntamenti", "pagamenti_oggi", "pagamenti_scaduti", "pagamenti_domani", "cantieri_attivi"]
  .map((k) => JSON.stringify(d?.[k] || [])).join("|");
const hhmm = (iso) => new Date(iso).toLocaleTimeString(localeTag(), { hour: "2-digit", minute: "2-digit" });

export default function AttivitaOggi() {
  const [b, setB] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(null);

  const running = useRef(false);
  const run = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    try { setB((await api.post("/agent/briefing/run?notify=false", null, { timeout: 150000 })).data); setFresh(null); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Report non disponibile, riprova"); }
    finally { setBusy(false); running.current = false; }
  }, []);

  // Il report salvato si usa solo se agenda e pagamenti non sono cambiati da quando è stato scritto.
  useEffect(() => {
    Promise.all([api.get("/agent/briefing/latest"), api.get("/agent/briefing/today")]).then(([l, t]) => {
      setCfg(l.data); setFresh(t.data);
      const x = l.data.briefing;
      if (x?.data === todayIso() && x?.dati && "email" in x.dati && sig(x.dati) === sig(t.data)) setB(x); else run();
    }).catch(run);
  }, [run]);
  const sections = b?.dati ? { ...b.dati, ...(fresh || {}), email: b.dati.email, whatsapp_non_letti: b.dati.whatsapp_non_letti } : null;

  const toggle = async (k, v) => {
    setCfg((c) => ({ ...c, [k === "briefing_enabled" ? "enabled" : "email"]: v }));
    await api.put("/agent/briefing/settings", { [k]: v }).catch(() => toast.error("Impostazione non salvata"));
  };

  return (
    <div className="space-y-5 animate-in-up" data-testid="today-page">
      <section className="rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-100 via-amber-50 to-orange-50 dark:from-amber-950/60 dark:via-amber-900/30 dark:to-slate-900 dark:border-amber-800 p-5 space-y-3 shadow-sm">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="w-11 h-11 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0"><Sun className="w-6 h-6" /></span>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-xl font-extrabold capitalize leading-tight text-amber-950 dark:text-amber-50">{longDate()}</div>
            <div className="text-xs text-amber-900/70 dark:text-amber-200/70" data-testid="today-updated">{b?.created_at ? `Aggiornato alle ${hhmm(b.created_at)} · assistente IA` : "Report dell'assistente IA"}</div>
          </div>
          <Button size="sm" variant="outline" onClick={run} disabled={busy} className="bg-white/70 dark:bg-transparent" data-testid="today-refresh-button">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1.5" /> Aggiorna</>}
          </Button>
        </div>
        {busy && !b
          ? <div className="flex items-center gap-2 text-sm text-amber-900 dark:text-amber-100" data-testid="today-loading"><Loader2 className="w-4 h-4 animate-spin" /> L'assistente sta preparando il report (circa 20-30 secondi)…</div>
          : <p className="text-[15px] leading-relaxed whitespace-pre-wrap text-amber-950 dark:text-amber-50" data-no-translate data-testid="today-ai-text">{b?.testo}</p>}
      </section>

      {sections && <ReportSections d={sections} />}

      {cfg && (
        <div className="rounded-xl border border-border bg-card p-4 flex flex-wrap items-center gap-4 text-sm" data-testid="today-settings">
          <span className="flex-1 min-w-[200px] text-muted-foreground">Ogni mattina alle 7:30 ricevi questo report con una notifica.</span>
          <label className="flex items-center gap-2"><Switch checked={cfg.enabled} onCheckedChange={(v) => toggle("briefing_enabled", v)} data-testid="today-enabled-switch" /> Attivo</label>
          <label className="flex items-center gap-2"><Switch checked={cfg.email} onCheckedChange={(v) => toggle("briefing_email", v)} data-testid="today-email-switch" /> Anche via email</label>
        </div>
      )}
    </div>
  );
}
